'use client';

import React, { useState, useRef, useEffect } from 'react';

export interface TabItem {
  id: string;
  label: string;
  content?: React.ReactNode;
  disabled?: boolean;
}

interface TabsProps {
  tabs: TabItem[];
  defaultTabId?: string;
  activeTabId?: string;
  onTabChange?: (id: string) => void;
  tabsPosition?: 'right' | 'left' | 'center';
  className?: string;
}

const tabsPositionMap: Record<string, string> = {
  center: 'justify-center',
  right: 'justify-start',
  left: 'justify-end',
};

const Tabs: React.FC<TabsProps> = ({
  tabs,
  defaultTabId,
  activeTabId,
  onTabChange,
  tabsPosition = 'right',
  className = '',
}) => {
  const isControlled = activeTabId !== undefined;
  const initialTab = defaultTabId ?? tabs.find(t => !t.disabled)?.id;
  const [internalActiveTab, setInternalActiveTab] = useState<string | undefined>(initialTab);
  const activeTab = isControlled ? activeTabId : internalActiveTab;

  const tabsRef = useRef<(HTMLButtonElement | null)[]>([]);
  const indicatorRef = useRef<HTMLSpanElement>(null);

  const [indicatorStyle, setIndicatorStyle] = useState<{
    left: number;
    width: number;
  }>({ left: 0, width: 0 });

  const hasContent = tabs.some(tab => tab.content !== undefined && tab.content !== null);
  const justifyClass = tabsPositionMap[tabsPosition] ?? 'justify-start';

  const handleTabClick = (id: string) => {
    if (isControlled) {
      onTabChange?.(id);
      return;
    }
    setInternalActiveTab(id);
    onTabChange?.(id);
  };

  useEffect(() => {
    if (!activeTab) return;

    const activeIndex = tabs.findIndex(t => t.id === activeTab);
    const activeTabEl = tabsRef.current[activeIndex];

    if (activeTabEl && indicatorRef.current) {
      const distanceFromRight =
        activeTabEl.parentElement!.offsetWidth -
        (activeTabEl.offsetLeft + activeTabEl.offsetWidth);

      setIndicatorStyle({
        left: distanceFromRight,
        width: activeTabEl.offsetWidth,
      });
    }
  }, [activeTab, tabs]);

  useEffect(() => {
    const handleResize = () => {
      if (!activeTab) return;
      const activeIndex = tabs.findIndex(t => t.id === activeTab);
      const activeTabEl = tabsRef.current[activeIndex];
      if (activeTabEl && indicatorRef.current && activeTabEl.parentElement) {
        const distanceFromRight =
          activeTabEl.parentElement.offsetWidth -
          (activeTabEl.offsetLeft + activeTabEl.offsetWidth);

        setIndicatorStyle({
          left: distanceFromRight,
          width: activeTabEl.offsetWidth,
        });
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, [activeTab, tabs]);

  if (!activeTab) return null;

  return (
    <div className={className} dir="rtl">
      <div
        role="tablist"
        aria-orientation="horizontal"
        className={`relative flex border-gray-200 ${justifyClass}`}
      >
        <span
          ref={indicatorRef}
          className="
            absolute bottom-0 h-1 bg-blue-700 rounded-t-full
            transition-all duration-300 ease-out
          "
          style={{
            right: 0,
            transform: `translateX(-${indicatorStyle.left}px)`,
            width: `${indicatorStyle.width}px`,
          }}
        />

        {tabs.map((tab, index) => {
          const isActive = tab.id === activeTab;

          return (
            <button
              key={tab.id}
              ref={el => { tabsRef.current[index] = el; }}
              role="tab"
              type="button"
              aria-selected={isActive}
              aria-controls={hasContent ? `tab-panel-${tab.id}` : undefined}
              id={`tab-${tab.id}`}
              disabled={tab.disabled}
              onClick={() => handleTabClick(tab.id)}
              className={`
                relative z-10 px-4 py-2 text-sm font-medium focus:outline-none transition-colors
                ${isActive
                  ? 'text-blue-700'
                  : 'text-gray-500 hover:text-gray-700'}
                ${tab.disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}
              `}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      {hasContent && (
        <div className="pt-4">
          {tabs.map(tab =>
            tab.id === activeTab ? (
              <div
                key={tab.id}
                role="tabpanel"
                id={`tab-panel-${tab.id}`}
                aria-labelledby={`tab-${tab.id}`}
              >
                {tab.content}
              </div>
            ) : null
          )}
        </div>
      )}
    </div>
  );
};

export default Tabs;
